import type { KeyboardEvent, MouseEvent } from 'react';

import { getPlanFeature, type PLAN_NAME } from '@readme/plans';

import useClassy from '@core/hooks/useClassy';
import usePlanPageUrl from '@core/hooks/usePlanPageUrl';
import Button from '@ui/Button';
import Icon from '@ui/Icon';

import PlanUpgradeContainer from '../PlanUpgradeContainer';

import classes from './index.module.scss';

interface PlanAccessBannerProps {
  className?: string;
  /**
   * Type of plan access messaging to display
   */
  kind: 'addon' | 'paygate' | 'trial';
  /**
   * Indicates which plan the feature being gated/trialed belongs to.
   */
  plan: `${PLAN_NAME}`;
  /**
   * Override the link target. Useful for contexts like editors where
   * navigating away would lose unsaved work.
   */
  target?: '_blank' | '_self';
}

/**
 * A banner we can display on features that are gated or in trial.
 * Usually used as tooltip content in conjunction with the PlanAccessBadge component, but can be used standalone.
 */
export default function PlanAccessBanner({ className, kind, plan, target }: PlanAccessBannerProps) {
  const bem = useClassy(classes, 'PlanAccessBanner');
  const planName = getPlanFeature(plan, 'name');
  const planPageUrl = usePlanPageUrl();

  // Prevent click events from bubbling up to the parent element
  // This is necessary when the banner is used as a tooltip within a dropdown to prevent the dropdown from closing
  // when the banner is clicked.
  const handlePropagation = (e: KeyboardEvent | MouseEvent) => e.stopPropagation();

  return (
    <div
      onClick={handlePropagation}
      onKeyPress={e => {
        if (e.key === 'Enter') {
          handlePropagation(e);
        }
      }}
      role="tooltip"
    >
      <PlanUpgradeContainer className={bem('&', className)}>
        <p className={bem('-description')}>
          {kind === 'paygate' ? (
            <>
              You’ll need to upgrade to the <strong>{planName}</strong> plan to access this feature.
            </>
          ) : kind === 'addon' ? (
            <>
              You&apos;ll need <strong>Pro plan or higher + Ask AI addon</strong> to access this feature.
            </>
          ) : (
            <>
              This is a <strong>{planName}</strong> plan feature, you&apos;ll have access during your trial.
            </>
          )}
        </p>
        <Button
          className={bem('-button')}
          href={planPageUrl.url}
          kind="primary"
          outline
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          size="sm"
          target={target ?? planPageUrl.target}
          uppercase={false}
        >
          View plans
          <Icon aria-hidden="true" className={bem('-button-icon')} name="arrow-right" />
        </Button>
      </PlanUpgradeContainer>
    </div>
  );
}
